import {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useSearch, useLocation } from "wouter";
import { useListRecipes } from "@workspace/api-client-react";
import type { RecipeRecord } from "@workspace/api-client-react";
import { Layout } from "@/components/Layout";
import { RecipeCard } from "@/components/RecipeCard";
import { PullToRefresh } from "@/components/PullToRefresh";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Search,
  BookOpen,
  Clock,
  X,
  ChevronDown,
  ChevronRight,
  SlidersHorizontal,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useNutritionToggle } from "@/contexts/NutritionToggleContext";
import type { NutritionSummary } from "@/components/NutritionLabel";
import { TagSelector } from "@/components/TagSelector";

const BASE = import.meta.env.BASE_URL?.replace(/\/$/, "") ?? "";

const RECENT_KEY = "cookbook_recent_searches";
const MAX_RECENT = 10;

function loadRecent(): string[] {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) ?? "[]");
  } catch {
    return [];
  }
}

function saveRecent(term: string) {
  const prev = loadRecent().filter((s) => s !== term);
  const next = [term, ...prev].slice(0, MAX_RECENT);
  localStorage.setItem(RECENT_KEY, JSON.stringify(next));
}

function clearRecent() {
  localStorage.removeItem(RECENT_KEY);
}

type SortOption =
  | "alpha"
  | "date"
  | "calories_asc"
  | "calories_desc"
  | "protein_desc"
  | "protein_asc"
  | "carbs_asc"
  | "carbs_desc"
  | "fat_asc"
  | "fat_desc";

const NUTRITION_SORTS: Set<SortOption> = new Set([
  "calories_asc",
  "calories_desc",
  "protein_desc",
  "protein_asc",
  "carbs_asc",
  "carbs_desc",
  "fat_asc",
  "fat_desc",
]);

/**
 * All free-form filter inputs are kept as strings (so empty = "" round-trips
 * cleanly through the URL). They're parsed to numbers only at request time.
 */
interface RangeFilters {
  calMin: string;
  calMax: string;
  proteinMin: string;
  proteinMax: string;
  carbsMin: string;
  carbsMax: string;
  fatMin: string;
  fatMax: string;
  maxPrepMinutes: string;
  maxCookMinutes: string;
}

const EMPTY_RANGES: RangeFilters = {
  calMin: "",
  calMax: "",
  proteinMin: "",
  proteinMax: "",
  carbsMin: "",
  carbsMax: "",
  fatMin: "",
  fatMax: "",
  maxPrepMinutes: "",
  maxCookMinutes: "",
};

function rangesActive(f: RangeFilters): boolean {
  return Object.values(f).some((v) => v.trim() !== "");
}

function parseNum(s: string): number | null {
  if (!s.trim()) return null;
  const n = parseFloat(s);
  return isNaN(n) ? null : n;
}

interface NutritionSummaryRow {
  recipeId: number;
  perServing: Record<string, number>;
  confidence: string | null;
  servingsUsed: number | null;
}

function toNutritionSummary(row: NutritionSummaryRow): NutritionSummary {
  const ps = row.perServing;
  return {
    calories: typeof ps.calories === "number" ? Math.round(ps.calories) : 0,
    protein_g:
      typeof ps.protein_g === "number"
        ? Math.round(ps.protein_g * 10) / 10
        : 0,
    total_carbs_g:
      typeof ps.total_carbs_g === "number"
        ? Math.round(ps.total_carbs_g * 10) / 10
        : 0,
    total_fat_g:
      typeof ps.total_fat_g === "number"
        ? Math.round(ps.total_fat_g * 10) / 10
        : 0,
  };
}

function nutritionSortComparator(
  nutritionMap: Map<number, NutritionSummary>,
  field: keyof NutritionSummary,
  dir: "asc" | "desc"
) {
  return (a: RecipeRecord, b: RecipeRecord) => {
    const na = nutritionMap.get(a.id);
    const nb = nutritionMap.get(b.id);
    if (!na && !nb) return a.title.localeCompare(b.title);
    if (!na) return 1;
    if (!nb) return -1;
    const va = na[field] as number;
    const vb = nb[field] as number;
    return dir === "asc" ? va - vb : vb - va;
  };
}

function applySort(
  recipes: RecipeRecord[],
  sort: SortOption,
  nutritionMap: Map<number, NutritionSummary>
): RecipeRecord[] {
  const sorted = [...recipes];
  switch (sort) {
    case "alpha":
      return sorted.sort((a, b) => a.title.localeCompare(b.title));
    case "date":
      return sorted.sort((a, b) => {
        const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return tb - ta;
      });
    case "calories_asc":
      return sorted.sort(nutritionSortComparator(nutritionMap, "calories", "asc"));
    case "calories_desc":
      return sorted.sort(nutritionSortComparator(nutritionMap, "calories", "desc"));
    case "protein_asc":
      return sorted.sort(nutritionSortComparator(nutritionMap, "protein_g", "asc"));
    case "protein_desc":
      return sorted.sort(nutritionSortComparator(nutritionMap, "protein_g", "desc"));
    case "carbs_asc":
      return sorted.sort(nutritionSortComparator(nutritionMap, "total_carbs_g", "asc"));
    case "carbs_desc":
      return sorted.sort(nutritionSortComparator(nutritionMap, "total_carbs_g", "desc"));
    case "fat_asc":
      return sorted.sort(nutritionSortComparator(nutritionMap, "total_fat_g", "asc"));
    case "fat_desc":
      return sorted.sort(nutritionSortComparator(nutritionMap, "total_fat_g", "desc"));
    default:
      return sorted;
  }
}

function RecipeCardSkeleton() {
  return (
    <div className="rounded-2xl bg-white border border-border/50 overflow-hidden shadow-sm">
      <Skeleton className="aspect-[4/3] rounded-none" />
      <div className="p-4 space-y-2.5">
        <Skeleton className="h-4 w-3/4 rounded-full" />
        <Skeleton className="h-3 w-1/2 rounded-full" />
        <div className="flex gap-1.5 pt-1">
          <Skeleton className="h-5 w-14 rounded-full" />
          <Skeleton className="h-5 w-16 rounded-full" />
        </div>
      </div>
    </div>
  );
}

function RangeInput({
  label,
  minVal,
  maxVal,
  unit,
  onMinChange,
  onMaxChange,
}: {
  label: string;
  minVal: string;
  maxVal: string;
  unit: string;
  onMinChange: (v: string) => void;
  onMaxChange: (v: string) => void;
}) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="w-16 text-xs font-medium text-muted-foreground shrink-0">
        {label}
      </span>
      <input
        type="number"
        min={0}
        placeholder="Min"
        value={minVal}
        onChange={(e) => onMinChange(e.target.value)}
        className="w-20 h-7 rounded-lg border border-border/60 bg-white px-2 text-xs focus:outline-none focus:ring-1 focus:ring-primary/40 focus:border-primary/40"
      />
      <span className="text-xs text-muted-foreground">to</span>
      <input
        type="number"
        min={0}
        placeholder="Max"
        value={maxVal}
        onChange={(e) => onMaxChange(e.target.value)}
        className="w-20 h-7 rounded-lg border border-border/60 bg-white px-2 text-xs focus:outline-none focus:ring-1 focus:ring-primary/40 focus:border-primary/40"
      />
      <span className="text-xs text-muted-foreground shrink-0">{unit}</span>
    </div>
  );
}

function SingleNumberInput({
  label,
  value,
  unit,
  onChange,
}: {
  label: string;
  value: string;
  unit: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="w-16 text-xs font-medium text-muted-foreground shrink-0">
        {label}
      </span>
      <span className="text-xs text-muted-foreground">≤</span>
      <input
        type="number"
        min={0}
        placeholder="Any"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-20 h-7 rounded-lg border border-border/60 bg-white px-2 text-xs focus:outline-none focus:ring-1 focus:ring-primary/40 focus:border-primary/40"
      />
      <span className="text-xs text-muted-foreground shrink-0">{unit}</span>
    </div>
  );
}

interface FilterPanelProps {
  ranges: RangeFilters;
  tags: string[];
  onRangesChange: (next: RangeFilters) => void;
  onTagsChange: (next: string[]) => void;
  onClear: () => void;
}

function FilterPanel({
  ranges,
  tags,
  onRangesChange,
  onTagsChange,
  onClear,
}: FilterPanelProps) {
  const [open, setOpen] = useState(false);
  const rActive = rangesActive(ranges);
  const active = rActive || tags.length > 0;

  function set(key: keyof RangeFilters) {
    return (v: string) => onRangesChange({ ...ranges, [key]: v });
  }

  const activeCount =
    Object.values(ranges).filter((v) => v.trim() !== "").length + tags.length;

  return (
    <div className="w-full max-w-xl">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "flex items-center gap-2 px-3 py-1.5 rounded-xl text-sm border transition-all w-full",
          active
            ? "bg-primary/10 border-primary/30 text-primary font-medium"
            : "bg-white border-border/60 text-muted-foreground hover:border-primary/30 hover:text-foreground"
        )}
      >
        <SlidersHorizontal className="w-3.5 h-3.5" />
        Filters
        {active && (
          <span className="inline-flex items-center justify-center min-w-4 h-4 px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold">
            {activeCount}
          </span>
        )}
        {open ? (
          <ChevronDown className="w-3.5 h-3.5 ml-auto" />
        ) : (
          <ChevronRight className="w-3.5 h-3.5 ml-auto" />
        )}
      </button>

      {open && (
        <div className="mt-2 bg-white border border-border/60 rounded-2xl p-4 shadow-sm space-y-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
              Tags
            </p>
            <TagSelector
              selected={tags}
              onChange={onTagsChange}
              placeholder="Filter by tag…"
            />
          </div>

          <div className="space-y-2 pt-2 border-t border-border/30">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Time
            </p>
            <SingleNumberInput
              label="Prep"
              value={ranges.maxPrepMinutes}
              unit="minutes"
              onChange={set("maxPrepMinutes")}
            />
            <SingleNumberInput
              label="Cook"
              value={ranges.maxCookMinutes}
              unit="minutes"
              onChange={set("maxCookMinutes")}
            />
          </div>

          <div className="space-y-2 pt-2 border-t border-border/30">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Nutrition (per serving)
            </p>
            <RangeInput
              label="Calories"
              minVal={ranges.calMin}
              maxVal={ranges.calMax}
              unit="cal"
              onMinChange={set("calMin")}
              onMaxChange={set("calMax")}
            />
            <RangeInput
              label="Protein"
              minVal={ranges.proteinMin}
              maxVal={ranges.proteinMax}
              unit="g"
              onMinChange={set("proteinMin")}
              onMaxChange={set("proteinMax")}
            />
            <RangeInput
              label="Carbs"
              minVal={ranges.carbsMin}
              maxVal={ranges.carbsMax}
              unit="g"
              onMinChange={set("carbsMin")}
              onMaxChange={set("carbsMax")}
            />
            <RangeInput
              label="Fat"
              minVal={ranges.fatMin}
              maxVal={ranges.fatMax}
              unit="g"
              onMinChange={set("fatMin")}
              onMaxChange={set("fatMax")}
            />
          </div>

          {active && (
            <button
              type="button"
              onClick={onClear}
              className="text-xs text-muted-foreground hover:text-destructive transition-colors flex items-center gap-1"
            >
              <X className="w-3 h-3" />
              Clear All Filters
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── URL <-> filter state plumbing ──────────────────────────────────────────
//
// The complete filter state lives in the URL so search results are
// shareable. Any change to a filter pushes a new query string via
// wouter's `navigate(..., { replace: true })`; the inverse is rebuilt on
// every render by reading `useSearch()`. We debounce range inputs
// (numeric typing) but apply tag toggles immediately.

function parseRangesFromUrl(p: URLSearchParams): RangeFilters {
  return {
    calMin: p.get("calMin") ?? "",
    calMax: p.get("calMax") ?? "",
    proteinMin: p.get("proteinMin") ?? "",
    proteinMax: p.get("proteinMax") ?? "",
    carbsMin: p.get("carbsMin") ?? "",
    carbsMax: p.get("carbsMax") ?? "",
    fatMin: p.get("fatMin") ?? "",
    fatMax: p.get("fatMax") ?? "",
    maxPrepMinutes: p.get("maxPrepMinutes") ?? "",
    maxCookMinutes: p.get("maxCookMinutes") ?? "",
  };
}

function buildUrl(
  q: string,
  ranges: RangeFilters,
  tags: string[],
  sort: SortOption,
): string {
  const p = new URLSearchParams();
  if (q.trim()) p.set("q", q.trim());
  for (const tag of tags) p.append("tag", tag);
  const map: Record<keyof RangeFilters, string> = {
    calMin: "calMin",
    calMax: "calMax",
    proteinMin: "proteinMin",
    proteinMax: "proteinMax",
    carbsMin: "carbsMin",
    carbsMax: "carbsMax",
    fatMin: "fatMin",
    fatMax: "fatMax",
    maxPrepMinutes: "maxPrepMinutes",
    maxCookMinutes: "maxCookMinutes",
  };
  for (const [k, urlKey] of Object.entries(map)) {
    const v = ranges[k as keyof RangeFilters];
    if (v.trim()) p.set(urlKey, v.trim());
  }
  if (sort !== "alpha") p.set("sort", sort);
  const qs = p.toString();
  return qs ? `/search?${qs}` : "/search";
}

export default function SearchPage() {
  const searchString = useSearch();
  const [, navigate] = useLocation();
  const { showNutrition, toggleNutrition } = useNutritionToggle();

  // Hydrate every piece of filter state from the URL once. From then on the
  // local state is the source of truth; URL writes happen on every change.
  const initialParams = useMemo(
    () => new URLSearchParams(searchString),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const [inputValue, setInputValue] = useState(
    () => initialParams.get("q") ?? "",
  );
  const [committedQuery, setCommittedQuery] = useState(
    () => initialParams.get("q") ?? "",
  );
  const [selectedTags, setSelectedTags] = useState<string[]>(
    () => initialParams.getAll("tag"),
  );
  const [ranges, setRanges] = useState<RangeFilters>(
    () => parseRangesFromUrl(initialParams),
  );
  const [debouncedRanges, setDebouncedRanges] = useState<RangeFilters>(
    () => parseRangesFromUrl(initialParams),
  );
  const [sortOption, setSortOption] = useState<SortOption>(
    () => (initialParams.get("sort") as SortOption | null) ?? "alpha",
  );

  const [recentSearches, setRecentSearches] = useState<string[]>(() =>
    loadRecent(),
  );
  const [showRecent, setShowRecent] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rangesDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const [nutritionMap, setNutritionMap] = useState<Map<number, NutritionSummary>>(
    new Map(),
  );

  const PAGE_SIZE = 30;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const fetchSummaries = useCallback(() => {
    return fetch(`${BASE}/api/nutrition/summaries`)
      .then((r) => r.json())
      .then((d: { summaries?: NutritionSummaryRow[] }) => {
        const map = new Map<number, NutritionSummary>();
        for (const row of d.summaries ?? []) {
          map.set(row.recipeId, toNutritionSummary(row));
        }
        setNutritionMap(map);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchSummaries();
  }, [fetchSummaries]);

  const isNutritionSort = NUTRITION_SORTS.has(sortOption);

  // ── URL sync ───────────────────────────────────────────────────────────────
  // A single helper centralizes URL writes so every piece of state ends up in
  // one shareable query string. Ranges use the debounced copy to avoid a URL
  // churn on every keystroke; tags / sort / search query commit immediately.
  const syncUrl = useCallback(
    (over?: {
      q?: string;
      tags?: string[];
      ranges?: RangeFilters;
      sort?: SortOption;
    }) => {
      navigate(
        buildUrl(
          over?.q ?? committedQuery,
          over?.ranges ?? debouncedRanges,
          over?.tags ?? selectedTags,
          over?.sort ?? sortOption,
        ),
        { replace: true },
      );
    },
    [committedQuery, debouncedRanges, selectedTags, sortOption, navigate],
  );

  const handleSortChange = (newSort: SortOption) => {
    setSortOption(newSort);
    if (NUTRITION_SORTS.has(newSort) && !showNutrition) {
      toggleNutrition();
    }
    syncUrl({ sort: newSort });
  };

  const handleRangesChange = (next: RangeFilters) => {
    setRanges(next);
    if (rangesDebounceRef.current) clearTimeout(rangesDebounceRef.current);
    rangesDebounceRef.current = setTimeout(() => {
      setDebouncedRanges(next);
      syncUrl({ ranges: next });
    }, 400);
  };

  const handleTagsChange = (next: string[]) => {
    setSelectedTags(next);
    syncUrl({ tags: next });
  };

  const handleClearFilters = () => {
    setRanges(EMPTY_RANGES);
    setDebouncedRanges(EMPTY_RANGES);
    setSelectedTags([]);
    if (rangesDebounceRef.current) clearTimeout(rangesDebounceRef.current);
    syncUrl({ ranges: EMPTY_RANGES, tags: [] });
  };

  // ── Server query: every filter is sent to /api/recipes so the heavy
  //    lifting (tag intersection, time/nutrition ranges, search) is done in
  //    indexed SQL rather than in the browser. The tag & sort params are
  //    forwarded verbatim; numeric ranges are parsed lazily.
  const queryParams = useMemo(() => {
    const params: Record<string, unknown> = {};
    if (committedQuery.trim()) params.search = committedQuery.trim();
    if (selectedTags.length > 0) params.tag = selectedTags;
    const fields: Array<[keyof RangeFilters, string]> = [
      ["calMin", "calMin"],
      ["calMax", "calMax"],
      ["proteinMin", "proteinMin"],
      ["proteinMax", "proteinMax"],
      ["carbsMin", "carbsMin"],
      ["carbsMax", "carbsMax"],
      ["fatMin", "fatMin"],
      ["fatMax", "fatMax"],
      ["maxPrepMinutes", "maxPrepMinutes"],
      ["maxCookMinutes", "maxCookMinutes"],
    ];
    for (const [k, apiKey] of fields) {
      const n = parseNum(debouncedRanges[k]);
      if (n != null) params[apiKey] = n;
    }
    // Server doesn't sort by nutrition — that's done client-side from the
    // separately-fetched nutrition summaries. For all other sorts we just
    // ignore server sort and resort client-side too (consistency).
    return params;
  }, [committedQuery, selectedTags, debouncedRanges]);

  const { data, isFetching, refetch } = useListRecipes(queryParams);

  const handleRefresh = useCallback(
    () => Promise.all([refetch(), fetchSummaries()]),
    [refetch, fetchSummaries],
  );

  const serverResults: RecipeRecord[] = data?.recipes ?? [];

  const commitSearch = useCallback(
    (term: string) => {
      const trimmed = term.trim();
      setCommittedQuery(trimmed);
      if (trimmed) {
        saveRecent(trimmed);
        setRecentSearches(loadRecent());
      }
      syncUrl({ q: trimmed });
    },
    [syncUrl],
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputValue(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      commitSearch(val);
    }, 300);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    commitSearch(inputValue);
    setShowRecent(false);
  };

  const handleRecentClick = (term: string) => {
    setInputValue(term);
    setShowRecent(false);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    commitSearch(term);
  };

  const handleClearHistory = () => {
    clearRecent();
    setRecentSearches([]);
    setShowRecent(false);
  };

  const handleClearInput = () => {
    setInputValue("");
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setCommittedQuery("");
    syncUrl({ q: "" });
    inputRef.current?.focus();
  };

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setShowRecent(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (rangesDebounceRef.current) clearTimeout(rangesDebounceRef.current);
    };
  }, []);

  // ── URL → state rehydration ─────────────────────────────────────────────
  // The URL is the source of truth for shareability, so rehydrate local
  // state whenever it changes from outside (browser back/forward, manual
  // edits). We compare normalized strings to avoid feedback loops with
  // our own `syncUrl` writes.
  const lastSyncedRef = useRef<string>(searchString);
  useEffect(() => {
    if (lastSyncedRef.current === searchString) return;
    lastSyncedRef.current = searchString;
    const p = new URLSearchParams(searchString);
    const q = p.get("q") ?? "";
    const tags = p.getAll("tag");
    const r = parseRangesFromUrl(p);
    const sort = (p.get("sort") as SortOption | null) ?? "alpha";
    setInputValue(q);
    setCommittedQuery(q);
    setSelectedTags(tags);
    setRanges(r);
    setDebouncedRanges(r);
    setSortOption(sort);
  }, [searchString]);

  // Keep our own URL writes in sync with the watcher above so they don't
  // trigger a redundant rehydration round-trip.
  useEffect(() => {
    lastSyncedRef.current = buildUrl(
      committedQuery,
      debouncedRanges,
      selectedTags,
      sortOption,
    ).split("?")[1] ?? "";
  }, [committedQuery, debouncedRanges, selectedTags, sortOption]);

  const filtersActive = rangesActive(debouncedRanges) || selectedTags.length > 0;
  const hasQuery = committedQuery.trim().length > 0;

  // Reset pagination when filters change.
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [committedQuery, debouncedRanges, sortOption, selectedTags]);

  const displayedRecipes = useMemo(
    () => applySort(serverResults, sortOption, nutritionMap),
    [serverResults, sortOption, nutritionMap],
  );

  const showSkeletons = isFetching;
  const showResults = !isFetching;
  const showContentResults =
    showResults && (hasQuery || filtersActive || isNutritionSort);
  const showRecentDropdown =
    showRecent && !inputValue && recentSearches.length > 0;

  return (
    <Layout>
      <PullToRefresh onRefresh={handleRefresh}>
      <div className="space-y-8 max-w-5xl mx-auto">
        <div className="text-center pt-4">
          <h1
            className="text-3xl md:text-4xl font-bold text-foreground mb-2"
            style={{ fontFamily: "var(--font-display)" }}
          >
            Search Recipes
          </h1>
          <p className="text-muted-foreground text-sm">
            Search by recipe name, ingredient, or author
          </p>
        </div>

        <div className="flex flex-col items-center gap-3">
          <form onSubmit={handleSubmit} className="w-full flex justify-center">
            <div ref={wrapperRef} className="relative w-full max-w-xl">
              <div className="relative">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                <Input
                  ref={inputRef}
                  placeholder="Search by recipe name, ingredient, or author"
                  value={inputValue}
                  onChange={handleInputChange}
                  onFocus={() => setShowRecent(true)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      setShowRecent(false);
                      inputRef.current?.blur();
                    }
                  }}
                  autoComplete="off"
                  className="pl-10 pr-10 h-12 rounded-2xl border-border/60 text-base shadow-sm focus-visible:ring-primary/30"
                />
                {inputValue && (
                  <button
                    type="button"
                    onClick={handleClearInput}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label="Clear search"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {showRecentDropdown && (
                <div className="absolute top-full left-0 right-0 mt-1.5 bg-white border border-border/60 rounded-2xl shadow-lg z-20 overflow-hidden">
                  <div className="flex items-center justify-between px-4 pt-3 pb-2">
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      Recent Searches
                    </span>
                    <button
                      type="button"
                      onClick={handleClearHistory}
                      className="text-xs text-muted-foreground hover:text-primary transition-colors"
                    >
                      Clear history
                    </button>
                  </div>
                  <div className="px-3 pb-3 flex flex-wrap gap-2">
                    {recentSearches.map((term) => (
                      <button
                        key={term}
                        type="button"
                        onClick={() => handleRecentClick(term)}
                        className={cn(
                          "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm",
                          "bg-muted hover:bg-primary/10 hover:text-primary text-muted-foreground",
                          "transition-colors border border-transparent hover:border-primary/20"
                        )}
                      >
                        <Clock className="w-3 h-3 flex-shrink-0" />
                        {term}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </form>

          <div className="flex flex-wrap items-center justify-center gap-3 w-full max-w-xl">
            <div className="flex items-center gap-2">
              <Switch
                id="show-nutrition"
                checked={showNutrition}
                onCheckedChange={toggleNutrition}
              />
              <Label
                htmlFor="show-nutrition"
                className="text-sm text-muted-foreground cursor-pointer select-none"
              >
                Show Nutrition
              </Label>
            </div>

            <div className="flex items-center gap-2">
              <label
                htmlFor="sort-select"
                className="text-sm text-muted-foreground select-none"
              >
                Sort:
              </label>
              <select
                id="sort-select"
                value={sortOption}
                onChange={(e) =>
                  handleSortChange(e.target.value as SortOption)
                }
                className="h-8 rounded-xl border border-border/60 bg-white px-2 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary/40 focus:border-primary/40"
              >
                <optgroup label="General">
                  <option value="alpha">A–Z</option>
                  <option value="date">Newest First</option>
                </optgroup>
                <optgroup label="By Nutrition (per serving)">
                  <option value="calories_asc">Calories: Low → High</option>
                  <option value="calories_desc">Calories: High → Low</option>
                  <option value="protein_desc">Protein: High → Low</option>
                  <option value="protein_asc">Protein: Low → High</option>
                  <option value="carbs_asc">Carbs: Low → High</option>
                  <option value="carbs_desc">Carbs: High → Low</option>
                  <option value="fat_asc">Fat: Low → High</option>
                  <option value="fat_desc">Fat: High → Low</option>
                </optgroup>
              </select>
            </div>
          </div>

          <FilterPanel
            ranges={ranges}
            tags={selectedTags}
            onRangesChange={handleRangesChange}
            onTagsChange={handleTagsChange}
            onClear={handleClearFilters}
          />
        </div>

        {showSkeletons && (
          <div className="space-y-4">
            <div className="h-4 w-24 rounded-full bg-muted animate-pulse" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {Array.from({ length: 6 }).map((_, i) => (
                <RecipeCardSkeleton key={i} />
              ))}
            </div>
          </div>
        )}

        {showContentResults && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {displayedRecipes.length === 0
                ? hasQuery
                  ? `No results for "${committedQuery}"${filtersActive ? " with these filters" : ""}`
                  : "No recipes match these filters"
                : hasQuery
                ? `${displayedRecipes.length} result${displayedRecipes.length !== 1 ? "s" : ""} for "${committedQuery}"`
                : `${displayedRecipes.length} recipe${displayedRecipes.length !== 1 ? "s" : ""}`}
            </p>

            {displayedRecipes.length > 0 ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {displayedRecipes.slice(0, visibleCount).map((recipe) => (
                    <RecipeCard
                      key={recipe.id}
                      recipe={recipe}
                      nutrition={nutritionMap.get(recipe.id) ?? null}
                    />
                  ))}
                </div>
                {displayedRecipes.length > visibleCount && (
                  <div className="flex flex-col items-center gap-2 pt-4">
                    <button
                      type="button"
                      onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                      className="inline-flex items-center justify-center rounded-xl border border-border bg-white px-5 py-2.5 text-sm font-semibold text-foreground hover:border-primary/40 hover:text-primary transition-all"
                    >
                      Load more ({displayedRecipes.length - visibleCount} remaining)
                    </button>
                    <button
                      type="button"
                      onClick={() => setVisibleCount(displayedRecipes.length)}
                      className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                    >
                      Show all {displayedRecipes.length}
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-20 gap-4 text-center">
                <div className="bg-muted rounded-2xl p-6">
                  <BookOpen className="w-10 h-10 text-muted-foreground mx-auto" />
                </div>
                <div>
                  <p className="text-lg font-semibold text-foreground">
                    No recipes found
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Try adjusting your search or filters
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {showResults && !showContentResults && (
          <div className="flex flex-col items-center justify-center py-20 gap-4 text-center">
            <div className="bg-muted/60 rounded-full p-6">
              <Search className="w-10 h-10 text-muted-foreground/50 mx-auto" />
            </div>
            <p className="text-muted-foreground text-sm">
              Start typing to search recipes, or use filters and sort to browse
            </p>
          </div>
        )}
      </div>
      </PullToRefresh>
    </Layout>
  );
}
